import { useEffect } from "react";

type CapacitorModule = {
	Capacitor: { isNativePlatform: () => boolean };
};

type AppModule = {
	App: {
		addListener: (
			event: string,
			cb: (data: { url: string }) => void,
		) => Promise<{ remove: () => void }>;
	};
};

type BrowserModule = {
	Browser: { close: () => Promise<void> };
};

/**
 * Parse a deep link URL for bridge token info.
 * Exported for testing.
 */
export function parseBridgeToken(rawUrl: string): {
	bridgeToken: string;
	workspaceSlug: string;
} | null {
	const url = new URL(rawUrl);
	const bridgeToken = url.searchParams.get("bridge_token");
	if (!bridgeToken) return null;

	// The redirect URL is /{workspaceSlug}/?bridge_token=xxx
	// Extract workspaceSlug from the first path segment
	const segments = url.pathname.split("/").filter(Boolean);
	const workspaceSlug = segments[0];
	if (!workspaceSlug) return null;

	return { bridgeToken, workspaceSlug };
}

export function useDeepLink(): void {
	useEffect(() => {
		let cleanup: (() => void) | undefined;

		(async () => {
			try {
				// Dynamic module paths bypass TS static resolution — these packages
				// live in native/package.json, not app/package.json.
				const coreMod = "@capacitor/core";
				const appMod = "@capacitor/app";
				const { Capacitor } = (await import(
					/* @vite-ignore */ coreMod
				)) as CapacitorModule;
				if (!Capacitor.isNativePlatform()) return;

				const { App } = (await import(/* @vite-ignore */ appMod)) as AppModule;
				const listener = await App.addListener(
					"appUrlOpen",
					async (event: { url: string }) => {
						const bridge = parseBridgeToken(event.url);
						if (bridge) {
							try {
								const res = await fetch(
									`/api/tenant/${bridge.workspaceSlug}/auth/exchange-token`,
									{
										method: "POST",
										credentials: "include",
										headers: { "Content-Type": "application/json" },
										body: JSON.stringify({
											bridgeToken: bridge.bridgeToken,
										}),
									},
								);

								if (res.ok) {
									// Close the in-app browser
									try {
										const browserMod = "@capacitor/browser";
										const { Browser } = (await import(
											/* @vite-ignore */ browserMod
										)) as BrowserModule;
										await Browser.close();
									} catch {
										// Browser plugin not available — no-op
									}

									// Navigate to the org home
									window.location.href = `/${bridge.workspaceSlug}/`;
									return;
								}
							} catch {
								// Exchange failed — fall through to default navigation
							}
						}

						// Default deep link behavior
						const url = new URL(event.url);
						window.location.href = url.pathname + url.search;
					},
				);

				cleanup = () => {
					listener.remove();
				};
			} catch {
				// Not in a Capacitor environment — no-op in browsers
			}
		})();

		return () => {
			cleanup?.();
		};
	}, []);
}
